/**
 * FlahaSOIL v2 — Projects list (Phase 8A).
 *
 * Top-level entry point for the project-centric workflow. Loads the
 * authenticated user's projects via `listProjects` and renders one
 * card per project with sample count and quick navigation. A
 * `+ New project` action opens a dialog that issues `createProject`
 * and pushes the user into the freshly-created project's detail page.
 */
import { keyframes } from "@emotion/react";
import {
	Alert,
	Box,
	Button,
	Chip,
	Grid,
	Skeleton,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ScienceIcon from "@mui/icons-material/Science";
import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import type {
	EntitlementSnapshot,
	ProjectSummaryDTO,
} from "@flaha/shared-types";

import { NewProjectDialog } from "../features/projects/components/NewProjectDialog";
import {
	canCreateUserProjects,
	isFreePlan,
} from "../features/plan/planSurface";
import { usePageHeader } from "../layouts/PageHeaderContext";
import { EmptyState } from "../layouts/components/EmptyState";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { getApiClient } from "../services/apiClientProvider";
import { useAuth } from "../auth";
import { reducedMotionMediaQuery } from "../theme/motionTokens";

const projectReveal = keyframes`
	from { opacity: 0; transform: translate3d(0, 8px, 0); }
	to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

/** Human-friendly "updated" label; falls back to the ISO date on parse failure. */
function formatUpdatedAt(iso: string): string {
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return iso;
	return date.toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

export function ProjectsListPage() {
	const navigate = useNavigate();
	const { status: sessionStatus, activeOrganization } = useAuth();
	const theme = useTheme();
	const isCompact = useMediaQuery(theme.breakpoints.down("sm"));
	const prefersReducedMotion = useMediaQuery(
		"(prefers-reduced-motion: reduce)",
		{ noSsr: true }
	);
	const [projects, setProjects] = useState<ProjectSummaryDTO[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [retryKey, setRetryKey] = useState(0);
	const [entitlements, setEntitlements] = useState<EntitlementSnapshot | null>(
		null
	);
	const allowNewProject = canCreateUserProjects(entitlements);
	const freePlan = isFreePlan(entitlements);

	usePageHeader({
		title: "My projects",
		subtitle: "Agronomic containers for soil samples and tests",
		breadcrumbs: [
			{ label: "Home", to: "/" },
			{ label: "Dashboard", to: "/dashboard" },
			{ label: "Projects" },
		],
	});

	useEffect(() => {
		if (sessionStatus !== "authenticated") return;
		let cancelled = false;
		void getApiClient()
			.getMyUsage()
			.then((res) => {
				if (!cancelled) setEntitlements(res.usage.entitlements);
			})
			.catch(() => {
				if (!cancelled) setEntitlements(null);
			});
		return () => {
			cancelled = true;
		};
	}, [sessionStatus, activeOrganization?.id]);

	useEffect(() => {
		if (sessionStatus !== "authenticated") return;
		let cancelled = false;
		setError(null);
		setProjects(null);
		void getApiClient()
			.listProjects({})
			.then((res) => {
				if (!cancelled) setProjects(res.projects);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setError(err instanceof Error ? err.message : String(err));
				}
			});
		return () => {
			cancelled = true;
		};
	}, [sessionStatus, activeOrganization?.id, retryKey]);

	const handleCreated = (projectId: string) => {
		setDialogOpen(false);
		navigate(`/projects/${projectId}`);
	};

	return (
		<Box
			data-testid="projects-workspace"
			data-layout={isCompact ? "compact" : "wide"}
			data-motion={prefersReducedMotion ? "reduced" : "full"}
			sx={{ minWidth: 0 }}
		>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				justifyContent="space-between"
				alignItems={{ xs: "stretch", sm: "center" }}
				gap={2}
				sx={{ mb: 3 }}
			>
				<Box>
					<Typography component="h1" variant="h4">
						Project workspace
					</Typography>
					<Typography color="text.secondary">
						{activeOrganization?.name ?? "Active organization"} · projects,
						samples, tests, and reports remain tenant-scoped.
					</Typography>
				</Box>
				{allowNewProject ? (
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={() => setDialogOpen(true)}
					>
						New project
					</Button>
				) : (
					<Button
						component={RouterLink}
						to="/pricing"
						variant="outlined"
					>
						Upgrade for more projects
					</Button>
				)}
			</Stack>

			{freePlan ? (
				<Alert severity="info" sx={{ mb: 2 }} data-testid="free-projects-banner">
					Your Free plan includes a system workspace named <strong>Free Tier</strong>{" "}
					for up to 5 Preliminary soil tests. Creating additional projects requires
					Professional.
				</Alert>
			) : null}

			{error && (
				<Alert
					severity="error"
					sx={{ mb: 2 }}
					action={
						<Button
							color="inherit"
							size="small"
							onClick={() => setRetryKey((key) => key + 1)}
						>
							Retry
						</Button>
					}
				>
					Failed to load projects: {error}
				</Alert>
			)}

			{projects === null && !error && (
				<Grid container spacing={2} aria-label="Loading projects" aria-busy="true">
					{[0, 1, 2].map((item) => (
						<Grid item xs={12} sm={6} lg={4} key={item}>
							<GlassSurface sx={{ p: 2.5 }}>
								<Skeleton width="62%" height={30} />
								<Skeleton width="36%" />
								<Skeleton height={52} />
							</GlassSurface>
						</Grid>
					))}
				</Grid>
			)}

			{projects !== null && projects.length === 0 && (
				<EmptyState
					titleId="projects-empty-title"
					icon={<FolderOpenIcon />}
					title="No projects yet"
					description="Create your first project to start adding soil samples and running soil tests."
					primaryAction={{
						label: "Create project",
						onClick: () => setDialogOpen(true),
					}}
					secondaryAction={{
						label: "Start soil analysis",
						to: "/soil-tests/new",
						variant: "outlined",
					}}
				/>
			)}

			{projects !== null && projects.length > 0 && (
				<Grid container spacing={2}>
					{projects.map((p) => (
						<Grid item xs={12} sm={6} lg={4} key={p.id}>
							<GlassSurface
								component="article"
								depth="raised"
								sx={{
									height: "100%",
									animation: `${projectReveal} ${theme.motion.duration.entry}ms ${theme.motion.easing.enter} both`,
									[reducedMotionMediaQuery]: { animation: "none" },
								}}
							>
								<Box
									component={RouterLink}
									to={`/projects/${p.id}`}
									aria-label={`Open project ${p.name}`}
									sx={{
										display: "block",
										height: "100%",
										p: 2.5,
										color: "inherit",
										textDecoration: "none",
										transition: theme.motion.transition.surface,
										"&:hover": { bgcolor: "rgba(255,255,255,0.035)" },
										"&:focus-visible": {
											outline: `3px solid ${theme.palette.workspace.blue}`,
											outlineOffset: -3,
										},
										[reducedMotionMediaQuery]: { transition: "none" },
									}}
								>
										<Stack direction="row" spacing={1} alignItems="center">
											<Typography component="h2" variant="h6" sx={{ flexGrow: 1 }}>
												{p.name}
											</Typography>
											<Chip
												label={p.status}
												size="small"
												color={p.status === "ACTIVE" ? "success" : "default"}
												variant={p.status === "ACTIVE" ? "filled" : "outlined"}
											/>
										</Stack>
										{p.code && (
											<Typography variant="caption" color="text.secondary">
												{p.code}
											</Typography>
										)}
										<Stack
											direction="row"
											spacing={0.75}
											alignItems="center"
											sx={{ mt: 1, color: "text.secondary" }}
										>
											<ScienceIcon fontSize="small" />
											<Typography variant="body2">
												{p.sampleCount} sample
												{p.sampleCount === 1 ? "" : "s"}
											</Typography>
										</Stack>
										<Stack
											direction="row"
											justifyContent="space-between"
											alignItems="center"
											sx={{ mt: 1.5 }}
										>
											<Typography variant="caption" color="text.secondary">
												Updated {formatUpdatedAt(p.updatedAt)}
											</Typography>
											<Stack
												direction="row"
												spacing={0.5}
												alignItems="center"
												sx={{ color: "primary.main" }}
											>
												<Typography variant="button">Open</Typography>
												<ArrowForwardIcon fontSize="small" />
											</Stack>
										</Stack>
								</Box>
							</GlassSurface>
						</Grid>
					))}
				</Grid>
			)}

			<NewProjectDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				onCreated={handleCreated}
			/>
		</Box>
	);
}
